import React, { useState, useEffect, useRef } from 'react';
import { Delete as DeleteIcon, Plus, Search } from '@icon-park/react';
import { 
  calculateNRV, 
  roundNutrientValue, 
  autoMatchUnit, 
  convertAndRound
} from '../../utils/nutritionUtils';
import { generateDefaultNutritionItemsFromRules } from '../../api/nutrition';
import { REQUIRED_NUTRIENTS, STANDARD_UNITS, getAllNutrientNames, getStandardUnit, convertUnit, isRequiredNutrient } from '../../config/nutrition.config';
import './styles/NutritionTable.css';

function NutritionTable({ data, onUpdate, onOpenStyle, onCopyStyleSame, onCopyStyleAll }) {
  /**
   * 营养成分表的比较基准（默认「每 100 克」）
   * 老标签数据没有 basis 字段时自动回落到 100 克，保证存量数据展示不变。
   */
  const basis = (data && data.basis) || { value: 100, unit: '克' };
  const handleBasisChange = (patch) => {
    onUpdate({ ...data, basis: { ...basis, ...patch } });
  };

  const [loading, setLoading] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [searchQueries, setSearchQueries] = useState({});
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [defaultItems, setDefaultItems] = useState(() => {
    // 初始默认值（如果无法从后端获取，使用前端配置）
    return REQUIRED_NUTRIENTS.map(name => ({
      name,
      per100g: '0',
      unit: getStandardUnit(name),
      nrv: '%'
    }));
  });

  // 从后端获取默认营养素列表
  useEffect(() => {
    const loadDefaultItems = async () => {
      try {
        setLoading(true);
        const items = await generateDefaultNutritionItemsFromRules();
        setDefaultItems(items);
        
        // 如果当前数据为空，使用从后端获取的默认列表
        if (!data.items || data.items.length === 0) {
          onUpdate({ ...data, items });
        }
      } catch (error) {
        console.error('加载默认营养素列表失败:', error);
        // 使用前端默认配置
      } finally {
        setLoading(false);
      }
    };

    loadDefaultItems();
  }, []); // 只在组件挂载时执行一次

  // 确保强制标示的营养素始终存在
  useEffect(() => {
    if (!data?.items || !onUpdate) return;
    const missing = REQUIRED_NUTRIENTS.filter(
      name => !data.items.some(item => item.name === name)
    );
    if (missing.length > 0) {
      const filledItems = [
        ...data.items,
        ...missing.map(name => ({
          name,
          per100g: '0',
          unit: getStandardUnit(name),
          nrv: '%'
        }))
      ];
      onUpdate({ ...data, items: filledItems });
    }
  }, [data?.items, onUpdate]);

  const nutritionItems = data.items || defaultItems;

  // 获取所有营养素名称列表
  const allNutrientNames = getAllNutrientNames();
  
  // 根据搜索查询过滤营养素名称
  const getFilteredNutrientNames = (index) => {
    const query = searchQueries[index] || '';
    if (!query) {
      return allNutrientNames;
    }
    return allNutrientNames.filter(name => name.includes(query));
  };

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownIndex(null);
        setSearchQueries({});
      }
    };

    if (openDropdownIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownIndex]);

  // 获取显示值（如果单位不是标准单位，需要转换显示）
  const getDisplayValue = (item) => {
    if (!item.name || !item.unit || !item.per100g || item.per100g === '0') {
      return item.per100g || '0';
    }
    
    const standardUnit = getStandardUnit(item.name);
    const currentUnit = item.unit;
    
    // 如果当前单位就是标准单位，直接返回
    if (currentUnit === standardUnit) {
      return item.per100g;
    }
    
    // 如果当前单位不是标准单位，尝试转换显示
    // 注意：这里假设per100g存储的是标准单位的值，需要转换到当前单位显示
    const convertedValue = convertUnit(item.per100g, standardUnit, currentUnit);
    if (convertedValue !== null) {
      return convertedValue.toString();
    }
    
    return item.per100g;
  };

  // 处理营养素名称选择
  const handleNutrientSelect = (index, selectedName) => {
    handleNutrientNameChange(index, selectedName);
    setOpenDropdownIndex(null);
    setSearchQueries(prev => {
      const newQueries = { ...prev };
      delete newQueries[index];
      return newQueries;
    });
  };

  // 处理营养素名称输入框点击
  const handleNutrientInputClick = (index) => {
    setOpenDropdownIndex(index);
    setSearchQueries(prev => ({ ...prev, [index]: '' }));
  };

  // 处理营养素名称输入框变化
  const handleNutrientInputChange = (index, value) => {
    handleNutrientNameChange(index, value);
    setOpenDropdownIndex(index);
    setSearchQueries(prev => ({ ...prev, [index]: value }));
  };

  // 处理搜索查询变化
  const handleSearchChange = (index, value) => {
    setSearchQueries(prev => ({ ...prev, [index]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...nutritionItems];
    const item = newItems[index];
    const nutrientName = item.name;
    
    // 处理不同字段的变更
    if (field === 'per100g') {
      // 修改每100克含量时，应用修约规则并自动计算NRV%
      const rawValue = value;
      
      // 如果营养素名称已设置，应用修约规则
      if (nutrientName) {
        const standardUnit = getStandardUnit(nutrientName);
        const currentUnit = item.unit || standardUnit;
        
        // 无论当前单位是什么，都要转换到标准单位存储
        let standardValue = rawValue;
        if (currentUnit !== standardUnit) {
          // 转换到标准单位
          const converted = convertUnit(rawValue, currentUnit, standardUnit);
          if (converted !== null) {
            standardValue = converted;
          }
        }
        
        // 应用修约规则（使用标准单位的值）
        const roundedValue = roundNutrientValue(standardValue, nutrientName);
        
        // 存储标准单位的值
        newItems[index][field] = roundedValue;
        
        // 计算NRV%（使用标准单位的值）
        const calculatedNRV = calculateNRV(nutrientName, roundedValue);
        newItems[index].nrv = calculatedNRV;
      } else {
        // 营养素名称未设置，直接保存原始值
        newItems[index][field] = rawValue;
      }
    } else if (field === 'unit') {
      // 修改单位时，自动匹配标准单位并转换数值
      const oldUnit = item.unit;
      const newUnit = value;
      const per100g = item.per100g;
      
      // 自动匹配标准单位
      let matchedUnit = newUnit;
      if (nutrientName) {
        matchedUnit = autoMatchUnit(newUnit, nutrientName);
      }
      
      newItems[index][field] = matchedUnit;
      
      // 如果已有含量值且单位发生变化，需要转换数值并重新计算NRV%
      if (nutrientName && per100g && per100g !== '0' && per100g !== '') {
        const standardUnit = getStandardUnit(nutrientName);
        
        // per100g存储的是标准单位的值，如果单位变化，需要转换显示值
        // 但这里我们需要重新计算：如果旧单位不是标准单位，需要先转换回标准单位
        // 然后再转换到新单位
        
        if (oldUnit !== matchedUnit && oldUnit && matchedUnit) {
          // 单位变化：需要将标准单位的值转换到新单位显示
          // 但per100g存储的是标准单位的值，所以不需要改变
          // 只需要重新计算NRV%（使用标准单位的值）
          const calculatedNRV = calculateNRV(nutrientName, per100g);
          newItems[index].nrv = calculatedNRV;
        } else if (matchedUnit === standardUnit) {
          // 如果新单位就是标准单位，应用修约规则并计算NRV%
          const roundedValue = roundNutrientValue(per100g, nutrientName);
          newItems[index].per100g = roundedValue;
          const calculatedNRV = calculateNRV(nutrientName, roundedValue);
          newItems[index].nrv = calculatedNRV;
        }
      }
    } else {
      // 其他字段直接更新
      newItems[index][field] = value;
    }
    
    onUpdate({ ...data, items: newItems });
  };

  const handleAddItem = () => {
    const newItems = [...nutritionItems];
    newItems.push({ 
      name: '', 
      per100g: '0', 
      unit: '克', // 默认单位，用户选择营养素名称后会自动更新
      nrv: '%'
    });
    onUpdate({ ...data, items: newItems });
  };
  
  // 当营养素名称改变时，自动设置标准单位并应用修约规则
  const handleNutrientNameChange = (index, name) => {
    const standardUnit = getStandardUnit(name);
    const newItems = [...nutritionItems];
    const oldName = newItems[index].name;
    
    newItems[index].name = name;
    if (standardUnit) {
      const oldUnit = newItems[index].unit;
      newItems[index].unit = standardUnit;
      
      // 如果单位发生变化，需要转换数值
      if (oldUnit && oldUnit !== standardUnit && newItems[index].per100g && newItems[index].per100g !== '0') {
        const convertedValue = convertAndRound(newItems[index].per100g, oldUnit, standardUnit, name);
        newItems[index].per100g = convertedValue;
      }
    }
    
    // 如果已有含量值，应用修约规则并重新计算NRV%
    if (newItems[index].per100g && newItems[index].per100g !== '0') {
      const roundedValue = roundNutrientValue(newItems[index].per100g, name);
      newItems[index].per100g = roundedValue;
      const calculatedNRV = calculateNRV(name, roundedValue);
      newItems[index].nrv = calculatedNRV;
    }
    
    onUpdate({ ...data, items: newItems });
  };

  const handleDeleteItem = (index) => {
    const newItems = nutritionItems.filter((_, i) => i !== index);
    onUpdate({ ...data, items: newItems });
  };

  return (
    <div className="nutrition-table-container">
      {loading && (
        <div style={{ padding: '8px', fontSize: '12px', color: '#666' }}>
          正在加载营养素标准配置...
        </div>
      )}
      <div className="template-header">
        <h3 className="template-title">输入产品营养成分信息</h3>
      </div>


      {/* 表格样式设置 */}
      <div className="nutrition-config-card">
        <div className="config-card-header">
          <span className="config-card-title">表格样式</span>
        </div>
        <div className="config-card-body">
          <div className="table-style-grid">
            <div className="style-item">
              <label className="style-label">表头背景</label>
              <div className="color-picker-wrapper">
                <input 
                  type="color" 
                  className="style-color-picker"
                  value={(data.tableStyle && data.tableStyle.headBgColor) || '#ffffff'} 
                  onChange={(e) => onUpdate({ ...data, tableStyle: { ...(data.tableStyle || {}), headBgColor: e.target.value } })} 
                />
                <span className="color-value-text">{(data.tableStyle && data.tableStyle.headBgColor) || '#ffffff'}</span>
              </div>
            </div>
            <div className="style-item">
              <label className="style-label">表头文字</label>
              <div className="color-picker-wrapper">
                <input 
                  type="color" 
                  className="style-color-picker"
                  value={(data.tableStyle && data.tableStyle.headTextColor) || '#5C4A37'} 
                  onChange={(e) => onUpdate({ ...data, tableStyle: { ...(data.tableStyle || {}), headTextColor: e.target.value } })} 
                />
                <span className="color-value-text">{(data.tableStyle && data.tableStyle.headTextColor) || '#5C4A37'}</span>
              </div>
            </div>
            <div className="style-item">
              <label className="style-label">行分隔线</label>
              <div className="color-picker-wrapper">
                <input 
                  type="color" 
                  className="style-color-picker"
                  value={(data.tableStyle && data.tableStyle.rowBorderColor) || '#D4BFA0'} 
                  onChange={(e) => onUpdate({ ...data, tableStyle: { ...(data.tableStyle || {}), rowBorderColor: e.target.value } })} 
                />
                <span className="color-value-text">{(data.tableStyle && data.tableStyle.rowBorderColor) || '#D4BFA0'}</span>
              </div>
            </div>
            <div className="style-item">
              <label className="style-label">文字颜色</label>
              <div className="color-picker-wrapper">
                <input 
                  type="color" 
                  className="style-color-picker"
                  value={(data.tableStyle && data.tableStyle.textColor) || '#202124'} 
                  onChange={(e) => onUpdate({ ...data, tableStyle: { ...(data.tableStyle || {}), textColor: e.target.value } })} 
                />
                <span className="color-value-text">{(data.tableStyle && data.tableStyle.textColor) || '#202124'}</span>
              </div>
            </div>
            <div className="style-item">
              <label className="style-label">单元格内边距</label>
              <div className="style-input-group">
                <input 
                  type="number" 
                  className="style-input-number"
                  min="4" 
                  max="24" 
                  value={(data.tableStyle && data.tableStyle.cellPadding) || 10} 
                  onChange={(e) => onUpdate({ ...data, tableStyle: { ...(data.tableStyle || {}), cellPadding: Number(e.target.value) } })} 
                />
                <span className="style-unit">px</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 营养成分数据 */}
      <div className="nutrition-config-card">
        <div className="config-card-header">
          <span className="config-card-title">营养成分数据</span>
          <span className="nutrition-count">共 {nutritionItems.length} 项</span>
        </div>
        <div className="config-card-body">
          <div className="nutrition-table">
            <div className="table-header">
              <div className="col-name">营养素名称</div>
              <div className="col-value">
                <span className="basis-editor">
                  <span className="basis-label">每</span>
                  <input
                    type="number"
                    min="1"
                    max="9999"
                    className="basis-input"
                    value={basis.value}
                    onChange={(e) => handleBasisChange({ value: e.target.value })}
                    title="比较基准的数值，默认 100"
                    aria-label="比较基准数值"
                  />
                  <select
                    className="basis-unit"
                    value={basis.unit}
                    onChange={(e) => handleBasisChange({ unit: e.target.value })}
                    title="比较基准的单位"
                    aria-label="比较基准单位"
                  >
                    <option value="克">克</option>
                    <option value="毫升">毫升</option>
                    <option value="份">份</option>
                  </select>
                </span>
              </div>
              <div className="col-nrv">NRV%</div>
              <div className="col-action">操作</div>
            </div>

        {nutritionItems.map((item, index) => (
          <div key={index} className={`table-row ${openDropdownIndex === index ? 'has-dropdown' : ''}`}>
            <div className="col-name">
              <div className="nutrient-name-wrapper" ref={openDropdownIndex === index ? dropdownRef : null}>
                <input
                  ref={openDropdownIndex === index ? inputRef : null}
                  type="text"
                  className="table-input"
                  value={item.name}
                  onChange={(e) => handleNutrientInputChange(index, e.target.value)}
                  onClick={() => handleNutrientInputClick(index)}
                  onFocus={() => handleNutrientInputClick(index)}
                  placeholder="营养素名称"
                />
                {openDropdownIndex === index && (
                  <div className="nutrient-dropdown nutrient-dropdown-top">
                    <div className="nutrient-search">
                      <Search theme="outline" size={14} fill="#80868b" />
                      <input
                        type="text"
                        className="nutrient-search-input"
                        placeholder="搜索营养素..."
                        value={searchQueries[index] || ''}
                        onChange={(e) => handleSearchChange(index, e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="nutrient-dropdown-list">
                      {getFilteredNutrientNames(index).length > 0 ? (
                        getFilteredNutrientNames(index).map((name) => (
                          <div
                            key={name}
                            className={`nutrient-dropdown-item ${item.name === name ? 'selected' : ''}`}
                            onClick={() => handleNutrientSelect(index, name)}
                          >
                            {name}
                          </div>
                        ))
                      ) : (
                        <div className="nutrient-dropdown-empty">未找到匹配的营养素</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="col-value">
              <div className="value-group">
                <input
                  type="number"
                  className="table-input-small"
                  step="any"
                  value={getDisplayValue(item)}
                  onChange={(e) => handleItemChange(index, 'per100g', e.target.value)}
                  onBlur={(e) => {
                    // 失焦时确保应用修约规则
                    if (item.name && e.target.value && e.target.value !== '0') {
                      handleItemChange(index, 'per100g', e.target.value);
                    }
                  }}
                />
                <input
                  type="text"
                  className="table-input-unit"
                  value={item.unit || ''}
                  onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                  placeholder="单位"
                  title="每100g的含量单位（如：克、毫克、千焦等），支持输入单位别名（如g、mg、kJ等）"
                  style={{ 
                    width: '60px', 
                    fontSize: '12px',
                    padding: '4px 6px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
            </div>
            <div className="col-nrv">
              <input
                type="text"
                className="table-input-small"
                value={item.nrv}
                onChange={(e) => handleItemChange(index, 'nrv', e.target.value)}
                placeholder="%"
                title={item.nrv !== '%' ? 'NRV%已自动计算，可手动修改' : '输入每100克含量后自动计算'}
                style={{ 
                  backgroundColor: item.nrv !== '%' ? '#f0f9ff' : '#ffffff',
                  cursor: item.nrv !== '%' ? 'text' : 'default'
                }}
              />
            </div>
            <div className="col-action">
              <button
                className="delete-btn"
                onClick={() => handleDeleteItem(index)}
                disabled={isRequiredNutrient(item.name)}
                title={isRequiredNutrient(item.name) ? '强制标示项不可删除' : '删除该营养素'}
              >
                <DeleteIcon theme="outline" size={14} fill="#333" strokeWidth={3} /> 删除
              </button>
            </div>
          </div>
        ))}
          </div>
        </div>
      </div>

      <button className="add-nutrition-btn" onClick={handleAddItem}>
        <span className="add-icon"><Plus theme="outline" size={18} fill="#333" strokeWidth={3} /></span>
        <span>添加营养素</span>
      </button>

      <div className="nutrition-note">
        <p className="note-text">根据GB 28050 食品安全国家标准 预包装食品营养标签通则编制</p>
      </div>
    </div>
  );
}

export default NutritionTable;
